import { useLoginInfo } from '@/components/LoginInfo/provider';
import {
  BaseOption,
  useModelsToOptionsWithData,
} from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import { uniqueArray } from '@/utils/uniqueArray';
import {
  FormSelect,
  FormSelectProps,
  LabelProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import {
  ApPayeeDetail,
  useGetApPayeesHook,
  useGetApPayeesSuspenseInfinite,
} from 'ap-openapi';
import classnames from 'classnames/bind';
import {
  KeyboardEvent,
  memo,
  RefObject,
  startTransition,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
  useFormState,
} from 'react-hook-form';
import styles from './ApPayee.module.scss';
import { PayeeInfo } from './PayeeInfo';

const cx = classnames.bind(styles);

export type ApPayeeRef = {
  findDetails: (
    payeeId: string | undefined
  ) => Promise<ApPayeeDetail | undefined>;
  undoSelection: () => void;
};

const INSTANT_PAYEE_VALUE = '1006';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<FormSelectProps<TFieldValues>['selectProps'], 'options'>;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  defaultOptions?: BaseOption[];
  include_inactive?: boolean;
  include_payee_id?: string;
  label?: LabelProps;
  instantPayeeActivatedValue?: string;
  useDisplayName?: boolean;
  inputRef?: RefObject<ApPayeeRef>;
  showCode?: boolean;
  hideInstantPayeeOnSearch?: boolean;
  captionField?: Array<keyof ApPayeeDetail>;
  multipleCaptionLine?: boolean;
  /* TODO:
    There are components that do not use FormContext, so I am passing them using props.
   */
  formContext?: {
    getValues: UseFormReturn<TFieldValues>['getValues'];
    setValue: UseFormReturn<TFieldValues>['setValue'];
    watch: UseFormReturn<TFieldValues>['watch'];
  };
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'>;

const _ApPayee = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  control,
  name,
  inputRef,
  formContext,
  useDisplayName = false,
  per = 25,
  include_inactive = true,
  include_payee_id,
  instantPayeeActivatedValue = '',
  showCode = false,
  hideInstantPayeeOnSearch = false,
  captionField,
  multipleCaptionLine = false,
  ...rest
}: Props<TFieldValues>) => {
  const { getValues, setValue, watch } = formContext ?? {};
  const [canceledOptions, setCanceledOptions] = useState<
    (BaseOption & { display_name: string })[]
  >([]);
  const [additionalPayeeData, setAdditionalPayeeData] = useState<
    ApPayeeDetail[]
  >([]);
  const [canceledPayeeDetail, setCanceledPayeeDetail] = useState<
    ApPayeeDetail | undefined
  >(undefined);
  const defaultOptions = useMemo(
    () => [...canceledOptions, ...(_defaultOptions ?? [])],
    [_defaultOptions, canceledOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [canceledSelected, setCanceledSelected] = useState<
    (BaseOption & { display_name: string }) | undefined
  >(undefined);
  const {
    data: apiData,
    error,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useGetApPayeesSuspenseInfinite(
    {
      query,
      per: per.toString(),
      include_inactive: include_inactive.toString(),
      include_payee_id: include_payee_id,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return url.searchParams.get('page');
        },
      },
    }
  );
  if (error) throw error;

  const mergedPayeeData = useMemo(() => {
    const apPayees = getPageData(apiData, 'ap_payees');
    let result = [...additionalPayeeData, ...apPayees];

    if (canceledSelected && canceledPayeeDetail) {
      result = [canceledPayeeDetail, ...result];
    } else if (canceledSelected) {
      const convertedCanceledOpt = {
        id:
          typeof canceledSelected.value === 'string'
            ? canceledSelected.value
            : String(canceledSelected.value),
        name: canceledSelected.label || '',
        display_name: useDisplayName ? canceledSelected.display_name : '',
      };
      result = [convertedCanceledOpt, ...result];
    }
    return result;
  }, [
    apiData,
    canceledSelected,
    canceledPayeeDetail,
    useDisplayName,
    additionalPayeeData,
  ]);

  // Determine which caption field to use
  const caption = useMemo(() => {
    // If captionField is explicitly provided, use it
    if (captionField) {
      return captionField;
    }
    // Fall back to current logic: use display_name if useDisplayName=true, otherwise use code
    return useDisplayName ? 'display_name' : 'code';
  }, [captionField, useDisplayName]);

  const options = useModelsToOptionsWithData<ApPayeeDetail>(
    mergedPayeeData,
    query,
    defaultOptions,
    {
      labelKeys: ['name'],
      caption,
      multipleCaptionLine,
    },
    showCode
  );

  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission on Enter key press
    }
  };

  const fixedInstantPayeeOptions = useMemo(() => {
    if (!instantPayeeActivatedValue) return options;

    const instantPayeeOption = {
      label: t('ap_payee_input_option_label_instant_payee'),
      value: instantPayeeActivatedValue,
    };
    const groupedOptions = [
      instantPayeeOption,
      {
        label: (
          <div>
            <span>{t('ap_payee_input_option_title_registered_master')}</span>
            <hr />
          </div>
        ),
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        options: options as BaseOption[],
      },
    ];

    // When hideInstantPayeeOnSearch=true (e.g. invoice_reports): show grouped only on
    // initial load or cleared search; show flat results during active search
    if (hideInstantPayeeOnSearch) {
      if (query === undefined || query === '') {
        return options.length > 0 ? groupedOptions : [instantPayeeOption];
      }
      return options;
    }

    // Default: always show instant payee option pinned above registered payees
    return groupedOptions;
  }, [options, instantPayeeActivatedValue, hideInstantPayeeOnSearch, query, t]);

  const { currentOffice, currentOfficeMember } = useLoginInfo();
  const isAdmin = currentOfficeMember?.officeMemberSetting.role === 'admin';
  const isAccountNumberMasked =
    currentOffice?.apOfficeSetting?.isAccountNumberMasked;
  const inputPlaceholder =
    !isAdmin && isAccountNumberMasked
      ? t('ap_payee_input_placeholder')
      : t('ap_payee_input_placeholder_with_account_number');
  const selectProps: SelectProps = useMemo(() => {
    const common = {
      onSearchDebounceWait: 600,
      comboBox: true,
      inputPlaceholder: inputPlaceholder,
      ..._selectProps,
      options: fixedInstantPayeeOptions,
      fetchMore: fetchMore,
      onSearch: onSearch,
      loading: isLoading,
      onKeyPress: handleKeyPress,
    };
    if (_selectProps?.mode === 'multiple') {
      return {
        ...common,
        mode: 'multiple',
      };
    } else {
      return {
        ...common,
        mode: 'single',
        selectAll: undefined,
        selectAllLabel: undefined,
      };
    }
  }, [
    _selectProps,
    fetchMore,
    isLoading,
    onSearch,
    inputPlaceholder,
    fixedInstantPayeeOptions,
  ]);

  // Get the API function for fetching payees
  const getApPayees = useGetApPayeesHook();

  // Function to find payee detail, first checking cache then making API request if needed
  const findApPayeeDetail = useCallback(
    async (payeeId: string): Promise<ApPayeeDetail | undefined> => {
      if (!payeeId) return undefined;

      // First, check if the payee exists in the current merged data (cache)
      const cachedPayee = mergedPayeeData.find((payee) => payee.id === payeeId);
      if (cachedPayee) {
        return cachedPayee;
      }

      try {
        // If not found in cache, make a fresh API request with include_payee_id
        const response = await getApPayees({
          include_payee_id: payeeId,
          per: '1', // We only need one result
        });

        // Find the specific payee in the response
        const payee = response.data.ap_payees?.find((p) => p.id === payeeId);
        return payee;
      } catch (error) {
        console.error('Failed to fetch payee detail from API:', error); // eslint-disable-line no-console
        return undefined;
      }
    },
    [mergedPayeeData, getApPayees]
  );

  // Function to fetch payee detail and update state
  const fetchPayeeDetail = useCallback(
    async (value: string, onSuccess: (detail: ApPayeeDetail) => void) => {
      if (!value) return;

      try {
        const payeeDetail = await findApPayeeDetail(value);
        if (payeeDetail) {
          onSuccess(payeeDetail);
        }
      } catch (error) {
        // same as useGetApPayeesSuspenseInfinite
        // TODO: handle error to show the message to toast notification
        console.error('Failed to fetch payee detail:', error); // eslint-disable-line no-console
        throw error;
      }
    },
    [findApPayeeDetail]
  );

  // Function to add payee data with duplicate check
  const addToAdditionalPayeeData = useCallback((payeeDetail: ApPayeeDetail) => {
    setAdditionalPayeeData((prev) => {
      const exists = prev.some((item) => item.id === payeeDetail.id);
      if (exists) return prev;
      return [payeeDetail];
    });
  }, []);

  // Function to set canceled selected
  const setCanceledSelectedFromDetail = useCallback(
    (payeeDetail: ApPayeeDetail, value: string) => {
      setCanceledSelected(() => ({
        label: payeeDetail.name ?? '',
        value: value,
        display_name: useDisplayName ? payeeDetail.display_name ?? '' : '',
      }));
      setCanceledPayeeDetail(payeeDetail);
    },
    [useDisplayName]
  );

  useImperativeHandle(
    inputRef,
    () => ({
      findDetails: async (
        payeeId: string | undefined
      ): Promise<ApPayeeDetail | undefined> => {
        if (!payeeId) return undefined;
        return await findApPayeeDetail(payeeId);
      },
      undoSelection: () => {
        if (canceledSelected && setValue) {
          setValue(
            name,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            canceledSelected.value as PathValue<
              TFieldValues,
              Path<TFieldValues>
            >,
            { shouldDirty: true }
          );
          const option = canceledSelected;
          setCanceledOptions((prev) => {
            // If spot payment (value = 1006) is selected for canceledSelected, no Option is added
            if (option.value === INSTANT_PAYEE_VALUE) return prev;
            return uniqueArray(prev, [option], 'value');
          });
        }
      },
    }),
    [findApPayeeDetail, name, setValue, canceledSelected]
  );
  const { onChange: onChangeBefore } = rest;
  const onWrapChangeBefore = useCallback(
    async (args: string | number | (string | number)[]) => {
      // Save current value to canceledSelected before change
      if (getValues) {
        const currentValue = getValues(name);
        if (currentValue) {
          await fetchPayeeDetail(String(currentValue), (detail) => {
            setCanceledSelectedFromDetail(detail, String(currentValue));
          });
        }
      }

      onChangeBefore?.(args);
    },
    [
      getValues,
      name,
      fetchPayeeDetail,
      setCanceledSelectedFromDetail,
      onChangeBefore,
    ]
  );

  // Watch form value changes for real-time updates (including external changes)
  const currentFormValue = watch ? watch(name) : undefined;

  const { errors } = useFormState({ control });
  const hasError = !!errors[name]?.message;

  const findPayeeDetailById = useCallback(
    (payeeId: string): ApPayeeDetail | undefined => {
      return mergedPayeeData.find((payee) => payee.id === payeeId);
    },
    [mergedPayeeData]
  );

  const currentPayeeDetail = useMemo(() => {
    if (!currentFormValue || currentFormValue === INSTANT_PAYEE_VALUE) {
      return undefined;
    }
    return findPayeeDetailById(String(currentFormValue));
  }, [currentFormValue, findPayeeDetailById]);

  useEffect(() => {
    if (!currentFormValue || currentFormValue === INSTANT_PAYEE_VALUE) return;

    const payeeId = String(currentFormValue);
    const existsInCache = !!findPayeeDetailById(payeeId);
    if (!existsInCache) {
      fetchPayeeDetail(payeeId, addToAdditionalPayeeData);
    }
  }, [
    currentFormValue,
    findPayeeDetailById,
    fetchPayeeDetail,
    addToAdditionalPayeeData,
  ]);

  return (
    <div className={cx(styles['container'])}>
      <FormSelect<TFieldValues>
        selectProps={selectProps}
        {...rest}
        control={control}
        name={name}
        onChange={onWrapChangeBefore}
      />
      {!hasError && currentPayeeDetail && (
        <PayeeInfo payeeDetail={currentPayeeDetail} />
      )}
    </div>
  );
};
_ApPayee.displayName = '_ApPayee';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApPayee = memo((props) => (
  <_ApPayee {...props} />
)) as typeof _ApPayee;
ApPayee.displayName = 'ApPayee';
